// PlantedBomb Effect (Epic Upgraded Version)
// Features: Bomb drops in on opponent, hovers and ticks, then detonates violently.
// Generates detailed jagged shrapnel, layered shockwaves, hundreds of fiery embers, and screen shake.

const CONFIG = {
    shrapnelCount: 22,
    emberCount: 250,
    scale: 1.5,
    approachDuration: 800,
    tickDuration: 1200,
    hoverAmplitude: 10,
    falloutDuration: 1500
};

// Detailed jagged shell fragments with inner bevels and panel lines
const shellFragments: Path2D[] = [
    new Path2D("M0,-45 C15,-45 25,-40 33,-30 L28,-22 L20,-28 Z M33,-30 C40,-20 44,-10 44,0 L35,5 L30,-15 Z"), // Top Right fragmented
    new Path2D("M0,-45 C-15,-45 -25,-40 -33,-30 L-28,-22 L-20,-28 Z M-33,-30 C-40,-20 -44,-10 -44,0 L-35,5 L-30,-15 Z"), // Top Left fragmented
    new Path2D("M44,0 C44,15 38,28 28,36 L20,25 L35,15 Z"), // Mid Right
    new Path2D("M-44,0 C-44,15 -38,28 -28,36 L-20,25 L-35,15 Z"), // Mid Left
    new Path2D("M28,36 C18,43 5,45 -5,44 L0,28 L15,25 Z"), // Bot Right
    new Path2D("M-28,36 C-18,43 -5,45 5,44 L0,28 L-15,25 Z"), // Bot Left
    new Path2D("M-10,-20 L15,-22 L22,-5 L10,12 L-12,15 L-25,-2 Z"), // Upper central plate (jagged)
    new Path2D("M-5,44 L5,44 L10,32 L-10,32 Z"), // Bottom cap
    new Path2D("M25,-15 L35,5 L20,25 L15,25 L30,-15 Z") // Inner support strata right
];

const innerBevels: Path2D[] = shellFragments.map(p => p); // We will stroke these with darker/lighter colors to imply thickness

// Highly detailed mechanical central eye
const centralEyeBase = new Path2D("M0,-24 A24,24 0 1,1 0,24 A24,24 0 1,1 0,-24");
const centralEyeGear = new Path2D("M0,-18 L3,-22 L7,-21 L8,-16 L13,-14 L18,-17 L21,-13 L17,-9 L18,-4 L22,-2 L22,3 L18,5 L17,10 L21,14 L18,18 L13,15 L8,17 L7,22 L2,22 L0,18 L-3,22 L-8,21 L-8,16 L-13,14 L-18,17 L-21,13 L-17,9 L-18,4 L-22,2 L-22,-3 L-18,-5 L-17,-10 L-21,-14 L-18,-18 L-13,-15 L-8,-17 L-7,-22 L-2,-22 Z");
const centralEyeLens = new Path2D("M0,-10 A10,10 0 1,0 0,10 A10,10 0 1,0 0,-10");

class Shrapnel {
    x: number; y: number; vx: number; vy: number;
    rot: number; vrot: number;
    path: Path2D; bevelPath: Path2D;
    life: number; size: number; baseGradientOffset: { x: number, y: number };

    constructor(startX: number, startY: number, pathIndex: number, scale: number) {
        this.x = startX;
        this.y = startY;

        // Extremely high velocity to fly off canvas
        const angle = Math.random() * Math.PI * 2;
        const speed = (20 + Math.random() * 30) * scale;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed - 15; // Stronger upward bias

        this.rot = Math.random() * Math.PI * 2;
        this.vrot = (Math.random() - 0.5) * 0.8; // Faster spin

        const idx = pathIndex % shellFragments.length;
        this.path = shellFragments[idx];
        this.bevelPath = innerBevels[idx];

        this.life = 1.0;
        this.size = scale * (0.5 + Math.random() * 0.8);

        this.baseGradientOffset = { x: (Math.random() - 0.5) * 20, y: (Math.random() - 0.5) * 20 };
    }

    update() {
        this.x += this.vx;
        this.y += this.vy;
        this.vy += 0.8; // Stronger gravity for fast arc
        this.vx *= 0.99; // Less air resistance so it flies off screen
        this.rot += this.vrot;
        this.life -= 0.005; // Lives much longer to ensure it leaves screen
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.scale(this.size, this.size);
        ctx.rotate(this.rot);
        ctx.globalAlpha = Math.max(0, Math.min(1, this.life * 2)); // Fade out only at the very end

        // Brushed metal gradient
        const xOffset = this.baseGradientOffset.x;
        const yOffset = this.baseGradientOffset.y;
        const grad = ctx.createLinearGradient(xOffset - 20, yOffset - 20, xOffset + 20, yOffset + 20);
        grad.addColorStop(0, '#5a5a60');
        grad.addColorStop(0.5, '#2a2a30');
        grad.addColorStop(1, '#111115');

        ctx.fillStyle = grad;
        ctx.fill(this.path);

        // Specular highlight edge
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.lineWidth = 1.5;
        ctx.stroke(this.bevelPath);

        // Inner dark shadow for thickness
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
        ctx.lineWidth = 3;
        ctx.setLineDash([5, 5]); // Simulate jagged break
        ctx.stroke(this.path);
        ctx.setLineDash([]);

        ctx.restore();
    }
}

class Ember {
    x: number; y: number; vx: number; vy: number;
    life: number; size: number; maxLife: number;
    colorType: number;

    constructor(startX: number, startY: number) {
        this.x = startX + (Math.random() - 0.5) * 40;
        this.y = startY + (Math.random() - 0.5) * 40;
        const angle = Math.random() * Math.PI * 2;
        // Epic speed
        const speed = 5 + Math.random() * 25;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed - 5;
        this.maxLife = 40 + Math.random() * 60;
        this.life = this.maxLife;
        this.size = 2 + Math.random() * 8;
        this.colorType = Math.random();
    }

    update() {
        this.x += this.vx;
        this.y += this.vy;
        this.vx *= 0.92;
        this.vy *= 0.92;
        this.vy -= 0.6; // Smoke/embers float up
        this.size *= 0.95;
        this.life -= 1;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        const opacity = Math.max(0, this.life / this.maxLife);
        ctx.globalAlpha = opacity;

        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);

        if (this.colorType > 0.8) {
            ctx.fillStyle = '#ffffff'; // White hot
            ctx.shadowColor = '#ffffaa';
        } else if (this.colorType > 0.4) {
            ctx.fillStyle = '#ffaa00'; // Orange/Yellow
            ctx.shadowColor = '#ff4400';
        } else {
            ctx.fillStyle = '#222222'; // Dark Smoke
            ctx.shadowColor = '#000000';
        }

        ctx.shadowBlur = this.colorType > 0.4 ? 15 : 0;
        ctx.fill();
        ctx.shadowBlur = 0;
    }
}

export type PlantedBombEffect = {
    kind: 'plantedBomb'
    t: number
    sx: number; sy: number
    tx: number; ty: number
    isOpponent: boolean
    data?: {
        phase: 'APPROACHING' | 'TICKING' | 'DETONATED'
        pos: { x: number, y: number }
        shrapnel: Shrapnel[]
        embers: Ember[]
        shockwave1: { r: number, o: number, thick: number }
        shockwave2: { r: number, o: number, thick: number }
        jitterX: number
        jitterY: number
    }
}

export const createPlantedBombEffect = (sx: number, sy: number, tx: number, ty: number, isOpponent: boolean = false): PlantedBombEffect => ({
    kind: 'plantedBomb', t: 0, sx, sy, tx, ty, isOpponent
});

export const renderPlantedBombEffect = (
    eff: PlantedBombEffect,
    ctx: CanvasRenderingContext2D
): boolean => {
    if (!eff.data) {
        eff.data = {
            phase: 'APPROACHING',
            pos: { x: eff.sx, y: eff.sy }, // Start at caster
            shrapnel: [],
            embers: [],
            shockwave1: { r: 0, o: 0, thick: 30 }, // Inner heavy orange ring
            shockwave2: { r: 0, o: 0, thick: 10 }, // Outer fast white ring
            jitterX: 0,
            jitterY: 0
        };
    }

    const d = eff.data;

    // Timeline logic
    if (eff.t < CONFIG.approachDuration) {
        // Toss in a heavy parabolic arc from caster to target
        const progress = eff.t / CONFIG.approachDuration;

        // Linear X translation
        d.pos.x = eff.sx + (eff.tx - eff.sx) * progress;

        // Y translation with a parabolic arc to simulate a heavy throw
        const arcHeight = 150;
        const arch = Math.sin(progress * Math.PI) * arcHeight;
        d.pos.y = eff.sy + (eff.ty - eff.sy) * progress - arch;
    }
    else if (eff.t < CONFIG.approachDuration + CONFIG.tickDuration) {
        if (d.phase === 'APPROACHING') d.phase = 'TICKING';

        // Hovering
        const tickProgress = (eff.t - CONFIG.approachDuration) / CONFIG.tickDuration;
        d.pos.y = eff.ty + Math.sin(tickProgress * Math.PI * 8) * CONFIG.hoverAmplitude;
    }
    else {
        if (d.phase === 'TICKING') {
            d.phase = 'DETONATED';

            // Initialize Epic Shockwaves
            d.shockwave1 = { r: 20, o: 1.0, thick: 60 };
            d.shockwave2 = { r: 40, o: 1.0, thick: 15 };

            // Spawn Epic Shrapnel Amount
            for (let i = 0; i < CONFIG.shrapnelCount; i++) {
                d.shrapnel.push(new Shrapnel(d.pos.x, d.pos.y, i, CONFIG.scale));
            }

            // Spawn Hundreds of Embers
            for (let i = 0; i < CONFIG.emberCount; i++) {
                d.embers.push(new Ember(d.pos.x, d.pos.y));
            }
        }
    }

    ctx.save();

    // Epic Screen Shake (Jitter)
    // Removed flashOpacity dependency, apply full jitter for a short duration
    if (d.phase === 'DETONATED' && eff.t < CONFIG.approachDuration + CONFIG.tickDuration + 500) {
        // Linearly decrease shake intensity over 500ms
        const timeSinceDetonation = eff.t - (CONFIG.approachDuration + CONFIG.tickDuration);
        const shakeFactor = 1.0 - (timeSinceDetonation / 500);
        const intensity = shakeFactor * 25; // Massive shake initially
        d.jitterX = (Math.random() - 0.5) * intensity;
        d.jitterY = (Math.random() - 0.5) * intensity;
        ctx.translate(d.jitterX, d.jitterY);
    }

    // RENDER: Approaching & Ticking Phases (The Intact Bomb)
    if (d.phase === 'APPROACHING' || d.phase === 'TICKING') {
        ctx.translate(d.pos.x, d.pos.y);
        ctx.scale(CONFIG.scale, CONFIG.scale);

        // Slow rotation of the entire bomb
        const rotationAngle = (eff.t * 0.001) % (Math.PI * 2);
        ctx.rotate(rotationAngle);

        // Internal glowing core
        let glowIntensity = 1.0;
        let expansion = 0;

        if (d.phase === 'TICKING') {
            const tickTime = eff.t - CONFIG.approachDuration;
            // Frequency increases linearly as it gets closer to detonation
            const freq = 1 + (tickTime / CONFIG.tickDuration) * 15;
            glowIntensity = 1.0 + Math.sin(tickTime * 0.01 * freq) * 0.8;
            expansion = Math.max(0, Math.sin(tickTime * 0.01 * freq) * 3); // Plates push out further
        }

        // Epic Core Gradient
        const coreGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, 48);
        coreGrad.addColorStop(0, `rgba(255, 255, 255, ${Math.min(1, glowIntensity * 1.5)})`);
        coreGrad.addColorStop(0.2, `rgba(255, 220, 100, ${Math.min(1, glowIntensity * 1.2)})`);
        coreGrad.addColorStop(0.6, `rgba(255, 100, 0, ${Math.min(1, glowIntensity)})`);
        coreGrad.addColorStop(1, `rgba(200, 20, 0, 0)`);

        ctx.beginPath();
        ctx.arc(0, 0, 50 + expansion, 0, Math.PI * 2);
        ctx.fillStyle = coreGrad;
        ctx.fill();

        // Draw Highly Detailed Shell Fragments
        shellFragments.forEach((path, idx) => {
            ctx.save();
            const angle = (idx / shellFragments.length) * Math.PI * 2;
            ctx.translate(Math.cos(angle) * expansion, Math.sin(angle) * expansion);

            // Per-fragment metallic gradient
            const fragGrad = ctx.createLinearGradient(-20, -20, 20, 20);
            fragGrad.addColorStop(0, '#6a6a70');
            fragGrad.addColorStop(0.5, '#3a3a40');
            fragGrad.addColorStop(1, '#111115');

            ctx.fillStyle = fragGrad;
            ctx.fill(path);

            // Specular highlighting
            ctx.strokeStyle = 'rgba(255,255,255,0.5)';
            ctx.lineWidth = 1;
            ctx.stroke(innerBevels[idx]);

            // Inner shadow for depth
            ctx.strokeStyle = 'rgba(0,0,0,0.8)';
            ctx.lineWidth = 2;
            ctx.stroke(path);

            // Draw Rivets
            ctx.fillStyle = '#999';
            ctx.beginPath();
            ctx.arc(Math.cos(angle) * 35, Math.sin(angle) * 35, 2, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = '#111';
            ctx.beginPath();
            ctx.arc(Math.cos(angle) * 35 + 0.5, Math.sin(angle) * 35 + 0.5, 1, 0, Math.PI * 2);
            ctx.fill();

            ctx.restore();
        });

        // Detailed Central Eye / Locking Mechanism
        ctx.save();
        // Counter-rotate the inner eye slightly
        ctx.rotate(-rotationAngle * 2);

        // Base plate
        ctx.fillStyle = '#1a1a1a';
        ctx.fill(centralEyeBase);
        ctx.strokeStyle = '#444';
        ctx.lineWidth = 3;
        ctx.stroke(centralEyeBase);

        // Gear ring
        ctx.fillStyle = '#333';
        ctx.fill(centralEyeGear);
        ctx.strokeStyle = '#666';
        ctx.lineWidth = 1;
        ctx.stroke(centralEyeGear);

        // Glowing structural lens
        const lensGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, 10);
        lensGrad.addColorStop(0, `rgba(255, 255, 255, ${Math.min(1, glowIntensity)})`);
        lensGrad.addColorStop(0.5, `rgba(255, ${150 - glowIntensity * 50}, 0, ${0.8 + glowIntensity * 0.2})`);
        lensGrad.addColorStop(1, '#220000');

        ctx.fillStyle = lensGrad;
        ctx.fill(centralEyeLens);

        ctx.shadowBlur = 20 + glowIntensity * 10;
        ctx.shadowColor = '#ff4400';
        ctx.strokeStyle = 'rgba(255, 100, 0, 0.8)';
        ctx.lineWidth = 2;
        ctx.stroke(centralEyeLens);
        ctx.shadowBlur = 0;

        ctx.restore();
    }

    // RENDER: Detonation Phase (Epic Explosion)
    if (d.phase === 'DETONATED') {
        // Epic Layered Shockwaves
        ctx.globalCompositeOperation = 'screen';

        // Outer fast white ring
        if (d.shockwave2.o > 0) {
            d.shockwave2.r += 40; // Extremely fast expansion
            d.shockwave2.o -= 0.03;
            ctx.beginPath();
            ctx.arc(d.pos.x, d.pos.y, d.shockwave2.r, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(255, 255, 255, ${Math.max(0, d.shockwave2.o)})`;
            ctx.lineWidth = d.shockwave2.thick * d.shockwave2.o;
            ctx.stroke();
        }

        // Inner heavy orange/red ring
        if (d.shockwave1.o > 0) {
            d.shockwave1.r += 25; // Slower, heavier expansion
            d.shockwave1.o -= 0.02;
            ctx.beginPath();
            ctx.arc(d.pos.x, d.pos.y, d.shockwave1.r, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(255, 80, 0, ${Math.max(0, d.shockwave1.o)})`;
            ctx.lineWidth = d.shockwave1.thick * d.shockwave1.o;
            ctx.stroke();

            // Core fiery burst
            const burstGrad = ctx.createRadialGradient(d.pos.x, d.pos.y, 0, d.pos.x, d.pos.y, d.shockwave1.r);
            burstGrad.addColorStop(0, `rgba(255, 255, 255, ${Math.max(0, d.shockwave1.o * 0.8)})`);
            burstGrad.addColorStop(0.2, `rgba(255, 150, 0, ${Math.max(0, d.shockwave1.o * 0.5)})`);
            burstGrad.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.fillStyle = burstGrad;
            ctx.fill();
        }

        ctx.globalCompositeOperation = 'source-over';

        // Update & Draw Embers/Smoke
        for (let i = d.embers.length - 1; i >= 0; i--) {
            d.embers[i].update();
            d.embers[i].draw(ctx);
            if (d.embers[i].life <= 0) d.embers.splice(i, 1);
        }

        // Update & Draw Epic Shrapnel
        for (let i = d.shrapnel.length - 1; i >= 0; i--) {
            d.shrapnel[i].update();
            d.shrapnel[i].draw(ctx);
            if (d.shrapnel[i].life <= 0) d.shrapnel.splice(i, 1);
        }
    }

    ctx.restore();

    // Check if effect is finished
    const totalDuration = CONFIG.approachDuration + CONFIG.tickDuration + CONFIG.falloutDuration;
    // Effect ends when time is up AND particles are gone
    if (eff.t > totalDuration && d.shrapnel.length === 0 && d.embers.length === 0) {
        return false;
    }

    return true;
};
